"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { StepNavigation } from "@/components/backoffice/step-navigation";
import { VehicleAssignmentStepProgress } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-progress";
import { VehicleAssignmentRetailContent } from "@/components/backoffice/cat/vehicle-assignment/retail/vehicle-assignment-retail-steps";
import { useRetailDraftStepNavigation } from "@/hooks/cat/use-retail-draft-step-navigation";
import { useMdmClientHealthCheck } from "@/hooks/use-mdm-client-health-check";
import { useVehicleAssignmentFlowCleanup } from "@/hooks/use-vehicle-assignment-flow-cleanup";
import { useDraftResume } from "@/hooks/cat/use-draft-resume";
import { useDraftLockHeartbeat } from "@/hooks/cat/use-draft-lock-heartbeat";
import { useMdmClientHealthStore } from "@/stores/mdm-client-health-store";
import { getRetailVehicleAssignmentVisibleDefinitions } from "@/lib/cat/vehicle-assignment-retail-step-definitions";
import {
  toVehicleAssignmentProgressSteps,
  type VehicleAssignmentFlowContext,
} from "@/lib/cat/vehicle-assignment-step-definitions";
import type { Kvps } from "@/lib/kvps";

function VehicleAssignmentRetailFlow({
  currentKvps,
}: {
  currentKvps?: Kvps | null;
}) {
  useVehicleAssignmentFlowCleanup();
  useDraftLockHeartbeat();
  useMdmClientHealthCheck();
  const mdmClientHealthErrorKind = useMdmClientHealthStore(
    (state) => state.errorKind,
  );
  const [canProceed, setCanProceed] = useState(true);

  const stepHealthErrors = useMemo<Record<string, boolean>>(
    () => ({
      client: mdmClientHealthErrorKind === "functional",
    }),
    [mdmClientHealthErrorKind],
  );

  const beforeNextHandlerRef = useRef<
    (() => boolean | void | Promise<boolean | void>) | null
  >(null);

  const registerBeforeNextHandler = useCallback(
    (handler: (() => boolean | void | Promise<boolean | void>) | null) => {
      beforeNextHandlerRef.current = handler;
    },
    [],
  );

  const ctx: VehicleAssignmentFlowContext = useMemo(
    () => ({ kvps: currentKvps ?? null }),
    [currentKvps],
  );

  const visibleDefinitions = useMemo(
    () => getRetailVehicleAssignmentVisibleDefinitions(ctx),
    [ctx],
  );

  const { goToPreviousStep, handleStepChange, step } =
    useRetailDraftStepNavigation(visibleDefinitions);

  const handleDraftResumeStep = useCallback(
    (stepIndex: number) => {
      handleStepChange(stepIndex);
    },
    [handleStepChange],
  );
  useDraftResume(handleDraftResumeStep);

  const stepsRetail = useMemo(
    () =>
      toVehicleAssignmentProgressSteps(visibleDefinitions).map((stepItem) => ({
        ...stepItem,
        hasError: Boolean(
          stepHealthErrors[visibleDefinitions[stepItem.step - 1]?.id ?? ""],
        ),
      })),
    [stepHealthErrors, visibleDefinitions],
  );

  const maxStep = stepsRetail.length;
  const minStep = 1;
  const currentStepId = visibleDefinitions[step - 1]?.id;
  const isSummaryValidationStep = currentStepId === "ctm_summary_validation";

  const runBeforeNextHandler = useCallback(async (): Promise<boolean> => {
    const handler = beforeNextHandlerRef.current;
    if (!handler) {
      return true;
    }

    try {
      const result = await handler();
      return result !== false;
    } catch {
      return false;
    }
  }, []);

  const handleNext = useCallback(async () => {
    const canAdvance = await runBeforeNextHandler();
    if (!canAdvance) {
      return;
    }
    handleStepChange(Math.min(step + 1, maxStep));
  }, [handleStepChange, maxStep, runBeforeNextHandler, step]);

  useEffect(() => {
    if (step > maxStep) {
      handleStepChange(maxStep);
    }
  }, [handleStepChange, maxStep, step]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-8">
        <div className="col-span-1">
          <VehicleAssignmentStepProgress
            currentStep={step}
            steps={stepsRetail}
          />
        </div>

        <div className="col-span-3">
          <VehicleAssignmentRetailContent
            currentKvps={currentKvps}
            onCanProceedChange={setCanProceed}
            onBeforeNextChange={registerBeforeNextHandler}
            onGoToPreviousStep={goToPreviousStep}
          />
          {!isSummaryValidationStep ? (
            <StepNavigation
              isNextButtonDisabled={!canProceed}
              onNext={() => void handleNext()}
              onPrevious={() => goToPreviousStep(Math.max(step - 1, minStep))}
              showNextButton={step < maxStep}
              showPreviousButton={step > minStep}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export { VehicleAssignmentRetailFlow };
